import React from 'react'
import { createPortal } from 'react-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Flex } from '@mobi/component-library/Misc/Flex'
import { colors, spacing } from '@mobi/component-library/Foundations'
import { PillButton } from '@mobi/component-library/Base/Buttons'
import { Icon } from '@mobi/component-library/Assets/Icon'
import { Tray } from '@mobi/component-library/Overlays/Tray'
import { Icon as IconLegacy } from '@mobi/component-library/Legacy/Icon'
import { StructureSearchGroupHeader } from '@core/Areas/StructuredSearch/Components/Common.styles'
import type {
  StructuredSearchFields,
  StructuredSearchTrack,
} from '@core/Areas/StructuredSearch/types'
import {
  ButtonGroupItemStyled,
  ButtonGroupStyled,
} from '@core/Components/Buttons/ButtonGroup.styles'
import { getFilteredTracks } from '@core/Areas/StructuredSearch/helpers/track-utils'
import { setSearchField, setSelectedTracks } from '@core/Areas/StructuredSearch/Store'
import { selectSearchCriteria } from '@core/Areas/StructuredSearch/Store/selectors'
import { SelectTracks, IconTypes } from './Components/SelectTracks'

export const LocationCriteria: React.FC<{
  availableTracks: StructuredSearchTrack[] | undefined
}> = ({ availableTracks }) => {
  const dispatch = useDispatch()

  const [isTrackSelectorOpen, setIsTrackSelectorOpen] = React.useState(false)

  const {
    locationCountryAustralia: aus,
    locationCountryNewZealand: nz,
    locationCountryInternational: int,
    selectedTracks,
  } = useSelector(selectSearchCriteria)

  const onChangeCallback = (
    field: keyof StructuredSearchFields,
    value: StructuredSearchFields[typeof field]
  ) => {
    dispatch(setSearchField({ field, value }))
  }

  const removeTrack = (id: string) => {
    if (selectedTracks) {
      const newSelectedTracks = selectedTracks.filter(track => track !== id)
      dispatch(setSelectedTracks(newSelectedTracks))
    }
  }

  const handleDismissTrackSelectorDrawer = () => {
    setIsTrackSelectorOpen(false)
  }

  const displayableTracks = getFilteredTracks(availableTracks || [], selectedTracks || [])

  return (
    <>
      <div>
        <StructureSearchGroupHeader>Location</StructureSearchGroupHeader>

        <ButtonGroupStyled>
          <ButtonGroupItemStyled
            id='location-country-australia'
            data-tid-location-country='Australia'
            selected={aus}
            onClick={() => onChangeCallback('locationCountryAustralia', !aus)}
          >
            Australia
          </ButtonGroupItemStyled>
          <ButtonGroupItemStyled
            id='location-country-new-zealand'
            data-tid-location-country='New Zealand'
            selected={nz}
            onClick={() => onChangeCallback('locationCountryNewZealand', !nz)}
          >
            New Zealand
          </ButtonGroupItemStyled>
          <ButtonGroupItemStyled
            id='location-country-international'
            data-tid-location-country='International'
            selected={int}
            onClick={() => onChangeCallback('locationCountryInternational', !int)}
          >
            International
          </ButtonGroupItemStyled>
        </ButtonGroupStyled>
      </div>

      <div>
        <StructureSearchGroupHeader>Track</StructureSearchGroupHeader>

        <Flex gap={spacing.smx2} flexWrap='wrap'>
          {displayableTracks?.map(track => {
            const iconType = IconTypes[track.contestType as keyof typeof IconTypes]
            return (
              <PillButton
                size='xs'
                onClick={() => removeTrack(track.id)}
                color='primary'
                key={track.id}
                data-testid={`LocationCriteria.Track.${track.id}`}
              >
                {track.name}
                {!track.isAustralian && <> ({track.location})</>}

                {track.isDuplicate && (
                  <IconLegacy size='2rem' type={iconType} color={colors.white} />
                )}

                <IconLegacy type='remove' size='1.1rem' />
              </PillButton>
            )
          })}

          <PillButton
            size='xs'
            onClick={() => setIsTrackSelectorOpen(true)}
            color='secondary_outline'
            data-tid-structured-search-location-chip='add-track'
          >
            <Icon name='DuocolorPlus' size='1.4rem' /> Add a Track
          </PillButton>
        </Flex>
      </div>

      {createPortal(
        <Tray
          headerTitle='Select Tracks'
          isOpen={isTrackSelectorOpen}
          onDismiss={handleDismissTrackSelectorDrawer}
        >
          <SelectTracks
            availableTracks={availableTracks}
            onDismiss={handleDismissTrackSelectorDrawer}
          />
        </Tray>,
        document.body
      )}
    </>
  )
}
